import { HttpClient, HttpContext } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, catchError, map, of, throwError } from "rxjs";
import { environment } from "../../../../environments/environment";
import { ASSET_TYPES, AssetType } from "../../../enums/assetTypes.enum";
import { REFERSH } from "../../../interceptors/cache/cache.interceptor";
import { TenantCompact, CustomerCompact, BuildingCompact, DoorCompact } from "../../../models/api/asset/assetCompact";
import { AssetHierarchyDTO } from "../../../models/api/asset/assetHierarchyDTO";
import { Parents, BotevoAssetCompact, BotevoAsset, BotevoAssetDTO } from "../../../models/api/asset/botevoAsset";
import { Building, BuildingDTO } from "../../../models/api/asset/building";
import { Customer, CustomerDTO } from "../../../models/api/asset/customer";
import { Door, DoorDTO } from "../../../models/api/asset/door";
import { LocationDTO, Location } from "../../../models/api/asset/location";
import { TenantDTO, Tenant } from "../../../models/api/tenant";
import { ResponseSuccess } from "../../../models/shared/responseSuccess";
import { getAssetParentType, isBotevoAssetType } from "../../../shared/utils/assetType.utils";
import { AssetHierarchyService } from "../asset-hierarchy/asset-hierarchy.service";
import { PermissionService } from "../permission/permission.service";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { Media } from "../../../models/api/media";
import { FloorMapSource } from "../../../models/ui/floorMapSource";

@Injectable({
  providedIn: "root",
})
export class AssetService {
  private http = inject(HttpClient);
  private assetHierarchyService = inject(AssetHierarchyService);
  private permissions = inject(PermissionService);
  private apiErrorHandler = inject(ApiErrorHandlerService);
  private PREDEFINDE_DOOR_IMAGES = ["gate_outside", "gate_inside", "motor", "control", "suspension", "name_plate"];

  /**
   * Finds the parents of a given asset in the hierarchy and returns them as an object
   * with the parent types as keys and the parent ids as values.
   *
   * @param hierarchy the asset hierarchy
   * @param targetId the id of the asset to find the parents for
   * @param targetType the type of the asset to find the parents for
   * @param parents the current parents object
   * @returns the parents object
   */
  private findAssetParents(
    hierarchy: AssetHierarchyDTO[],
    targetId: string,
    targetType: string | null,
    parents: Parents = {}
  ): Parents | null {
    for (const asset of hierarchy) {
      if (asset.id === targetId && asset.assetType === targetType) {
        return parents;
      }
      if (asset.children && asset.children.length > 0) {
        const result = this.findAssetParents(asset.children, targetId, targetType, {
          ...parents,
          [asset.assetType.toLowerCase()]: { id: asset.id },
        });
        if (result) {
          return result;
        }
      }
    }
    return null;
  }

  private findDoorIds(hierarchy: AssetHierarchyDTO[]): string[] {
    const doors: string[] = [];
    for (const asset of hierarchy) {
      if (asset.assetType === ASSET_TYPES.DOOR) {
        doors.push(asset.id);
      }
      if (asset.children && asset.children.length > 0) {
        doors.push(...this.findDoorIds(asset.children).filter((id) => id));
      }
    }
    return doors;
  }

  public getAllDoors(): Observable<string[]> {
    return this.assetHierarchyService.getAssetHierarchy(ASSET_TYPES.DOOR, 4).pipe(
      map((res) => {
        if (res) {
          return this.findDoorIds(res);
        } else {
          return [];
        }
      })
    );
  }
  public getAssets(
    type: ASSET_TYPES.TENANT,
    parentId?: string,
    parentType?: string,
    refresh?: boolean
  ): Observable<TenantCompact[]>;
  public getAssets(
    type: ASSET_TYPES.LOCATION,
    parentId?: string,
    parentType?: string,
    refresh?: boolean
  ): Observable<CustomerCompact[]>;
  public getAssets(
    type: ASSET_TYPES.BUILDING,
    parentId?: string,
    parentType?: string,
    refresh?: boolean
  ): Observable<BuildingCompact[]>;
  public getAssets(
    type: ASSET_TYPES.DOOR,
    parentId?: string,
    parentType?: string,
    refresh?: boolean
  ): Observable<DoorCompact[]>;
  public getAssets(
    type: string | null,
    parentId?: string,
    parentType?: string,
    refresh?: boolean
  ): Observable<BotevoAssetCompact[]>;
  public getAssets(
    type: ASSET_TYPES | null | string,
    parentId?: string,
    parentType?: string,
    refresh = false
  ): Observable<BotevoAssetCompact[]> {
    return this.http
      .get<BotevoAssetCompact[]>(environment.baseUrl + `/asset`, {
        params: {
          assetType: type ?? "",
          parentId: parentId ?? "",
          parentType: parentType ?? "",
        },
        context: new HttpContext().set(REFERSH, refresh),
      })
      .pipe(
        this.apiErrorHandler.handle(type?.toLowerCase(), []),
        map((res) => {
          return this.orderByLabel(res) ?? [];
        })
      );
  }

  /**
   * Orders the given array of assets alphabetically by their label.
   * If either asset is missing a label, the order is not changed.
   * @param assets The array of assets to order.
   * @returns The ordered array of assets.
   */
  private orderByLabel(assets: BotevoAssetCompact[]) {
    return assets.sort((a, b) => {
      if (a.label && b.label) {
        return a.label.localeCompare(b.label);
      } else {
        return 0;
      }
    });
  }

  /**
   * Creates a new asset.
   * @param assetType The type of the asset to create.
   * @param asset The asset to create.
   * @param parentId The id of the parent asset, if applicable.
   * @returns An observable that emits the id of the created asset, or null if the creation failed.
   */
  public createAsset(
    assetType: AssetType,
    asset: BotevoAsset,
    parentId: string | undefined
  ): Observable<string | null> {
    if (parentId && (asset instanceof Door || asset instanceof Building)) {
      const parentType = getAssetParentType(assetType);

      const parents = this.findAssetParents(this.assetHierarchyService.assetHierarchy, parentId, parentType);

      if (parents) {
        asset.parents = parents;
      }
    }

    return this.http.post<BotevoAsset>(environment.baseUrl + "/asset?assetType=" + assetType, asset.values).pipe(
      this.apiErrorHandler.handle(assetType.toLowerCase()),
      map((res: BotevoAsset) => {
        this.assetHierarchyService.refreshAssetHierarchy();
        return res.id ?? null;
      })
    );
  }

  /**
   * Edits an existing asset of the specified type.
   * Sends a PUT request to update the asset's information on the server.
   * If the update is successful, emits an observable with a success response.
   * Otherwise, emits an observable with a failure response.
   *
   * @param assetType The type of the asset to edit.
   * @param asset The asset object containing updated values.
   * @returns An observable that emits a success response if the update was successful,
   * otherwise emits a failure response.
   */

  public editAsset(assetType: string, asset: BotevoAsset): Observable<string | null> {
    if (
      asset instanceof Door &&
      this.permissions.missesPermission(this.permissions.allPermissions.ASSET_MASTER_DATA_CREATE)
    ) {
      delete asset.gateHistoryCycle;
      delete asset.activeDate;
    }
    return this.http
      .put<BotevoAsset>(environment.baseUrl + "/asset/" + asset.id + "?assetType=" + assetType, asset.values)
      .pipe(
        this.apiErrorHandler.handle(assetType.toLowerCase()),

        map((res) => {
          this.assetHierarchyService.refreshAssetHierarchy();
          return res.id ?? null;
        })
      );
  }

  /**
   * Deletes an asset of the given type and id.
   * @param assetType The type of the asset to delete.
   * @param assetId The id of the asset to delete.
   * @returns An observable that emits a success response if the deletion was successful,
   * otherwise emits a failure response.
   */

  public deleteAsset(assetType: string, assetId: string): Observable<ResponseSuccess> {
    return this.http
      .delete(environment.baseUrl + "/asset/" + assetId + "?assetType=" + assetType, {
        observe: "response",
      })
      .pipe(
        this.apiErrorHandler.handle(assetType.toLowerCase()),
        map((res) => {
          if (res.status === 200) {
            this.assetHierarchyService.refreshAssetHierarchy();
            return { success: true };
          } else {
            return { success: false };
          }
        })
      );
  }

  /**
   * Returns an observable that emits an empty asset of the specified type.
   * If a parent asset is provided, it is used to populate the appropriate fields.
   * @param assetType The type of the asset to create.
   * @param parent The parent asset, if applicable.
   * @returns An observable that emits an empty asset of the specified type.
   */
  public getEmptyAsset(assetType: ASSET_TYPES.CUSTOMER): Observable<Customer>;
  public getEmptyAsset(assetType: ASSET_TYPES.LOCATION, parent?: BotevoAsset): Observable<Location>;
  public getEmptyAsset(assetType: ASSET_TYPES.BUILDING, parent?: BotevoAsset): Observable<Building>;
  public getEmptyAsset(assetType: ASSET_TYPES.DOOR, parent?: BotevoAsset): Observable<Door>;
  public getEmptyAsset(assetType: string, parent?: BotevoAsset): Observable<BotevoAsset | null>;
  public getEmptyAsset(assetType: ASSET_TYPES | string, parent?: BotevoAsset): Observable<BotevoAsset | null> {
    switch (assetType) {
      case ASSET_TYPES.CUSTOMER:
        return of(new Customer({ label: "", address: { country: "DE" } }));
      case ASSET_TYPES.LOCATION:
        return of(
          new Location({
            label: "",
            customer: { id: parent?.id },
            address: { country: "DE" },
          })
        );
      case ASSET_TYPES.BUILDING: {
        const parentLocation = parent;
        if (parentLocation instanceof Location) {
          const buildingObject: BuildingDTO = {
            label: "",
            location: { id: parent?.id },
            address: parentLocation?.address,
          };
          if (parentLocation?.floorMapUrl && buildingObject.location) {
            buildingObject.location.locationFloorMapUrl = parentLocation.floorMapUrl;
          }
          return of(new Building(buildingObject));
        } else {
          return throwError(() => "Parent is not a location");
        }
      }
      case ASSET_TYPES.DOOR: {
        const parentBuilding = parent as Building;
        const doorObject: DoorDTO = {
          label: "",
          building: { id: parent?.id },
          isSluiceGate: false,
          quickupGate: "none",
          loadingBridge: "none",
          emailEnergyLoss: false,
          hasButler: false,
          core: true,
        };
        if (parentBuilding.floorMapUrl && doorObject.building) {
          doorObject.building.buildingFloorMapUrl = parentBuilding.floorMapUrl;
        }
        return of(new Door(doorObject));
      }
      default:
        return throwError(() => "Invalid asset type");
    }
  }

  /**
   * Fetches the details of an asset by its ID and type.
   * @param assetId The ID of the asset to retrieve.
   * @param assetType The type of the asset to retrieve.
   * @param refresh Optional flag to refresh the asset details.
   * @returns An observable that emits the asset details as a BotevoAsset.
   * @throws An error if the response does not contain a body.
   */
  public getAssetDetails(assetId: string, assetType: string, refresh = false): Observable<BotevoAsset | null> {
    if (isBotevoAssetType(assetType)) {
      return this.http
        .get<TenantDTO | CustomerDTO | LocationDTO | BuildingDTO | DoorDTO>(environment.baseUrl + `/asset/${assetId}`, {
          params: {
            assetType: assetType ? assetType : "",
          },
          context: new HttpContext().set(REFERSH, refresh),
        })
        .pipe(
          this.apiErrorHandler.handle(assetType.toLowerCase(), null),
          map((res) => {
            if (res) {
              return this.convertToAsset(assetType, res);
            } else {
              return null;
            }
          })
        );
    } else {
      return throwError(() => "Invalid asset type");
    }
  }

  public getDoorImages(doorId: string): Observable<Media[]> {
    return this.http
      .get<Media[]>(`${environment.baseUrl}/gateSystem/${doorId}/images`)
      .pipe(this.apiErrorHandler.handle("file"));
  }

  public addDoorImage(doorId: string, file: File | null, label: string) {
    const formData = new FormData();
    if (file) {
      formData.append("file", file);
    }
    formData.append("label", label);

    return this.http.post<Media[]>(`${environment.baseUrl}/gateSystem/${doorId}/images`, formData);
  }

  public replaceDoorImage(doorId: string, file: File | null, mediaId: string) {
    const formData = new FormData();
    if (file) formData.append("file", file);
    return this.http
      .put<Media>(`${environment.baseUrl}/gateSystem/${doorId}/images/${mediaId}`, formData)
      .pipe(this.apiErrorHandler.handle("file"));
  }

  public patchDoorImage(doorId: string, mediaId: string, label: string) {
    return this.http
      .patch<Media[]>(`${environment.baseUrl}/gateSystem/${doorId}/images/${mediaId}`, { label })
      .pipe(this.apiErrorHandler.handle("file"));
  }

  public deleteDoorImage(doorId: string, mediaId: string) {
    return this.http
      .delete(`${environment.baseUrl}/gateSystem/${doorId}/images/${mediaId}`)
      .pipe(this.apiErrorHandler.handle("file"));
  }

  public getAvailableImageTypes(existingTypes: string[], currentType?: string) {
    const availableTypes = this.PREDEFINDE_DOOR_IMAGES.filter((type) => !existingTypes.includes(type));
    if (currentType) {
      availableTypes.push(currentType);
    }
    availableTypes.push("other");
    return availableTypes;
  }

  /**
   * Checks if the given image label is a predefined gate image type.
   *
   * @param imageLabel - The label of the image to check.
   * @returns True if the image label is a predefined gate image type, false otherwise.
   */
  public isPredefindedDoorImage(imageLabel: string) {
    return this.PREDEFINDE_DOOR_IMAGES.includes(imageLabel);
  }

  public getBuildingFloormap(buildingId: string): Observable<FloorMapSource | null> {
    return this.http
      .get(`${environment.baseUrl}/building/${buildingId}/floorMap`, {
        responseType: "blob",
        observe: "response",
      })
      .pipe(
        this.apiErrorHandler.handle("file"),
        catchError(() => of(null)),
        map((floormapSource) => {
          if (floormapSource) {
            const contentType = floormapSource.headers.get("content-type") ?? null;
            const type: "image" | "pdf" | null = this.parseFloormapType(contentType);
            return { type, source: floormapSource.body };
          } else {
            return null;
          }
        })
      );
  }

  public editBuildingFloormap(buildingId: string, file: File | null) {
    const formData = new FormData();
    if (file) {
      formData.append("file", file);
    }

    return this.http
      .put<Media>(`${environment.baseUrl}/building/${buildingId}/floorMap`, formData)
      .pipe(this.apiErrorHandler.handle("file"));
  }

  public editLocationFloormap(locationId: string, file: File | null) {
    const formData = new FormData();
    if (file) {
      formData.append("file", file);
    }

    return this.http
      .put<Media>(`${environment.baseUrl}/location/${locationId}/floorMap`, formData)
      .pipe(this.apiErrorHandler.handle("file"));
  }

  public getLocationFloormap(locationId: string): Observable<FloorMapSource | null> {
    return this.http
      .get(`${environment.baseUrl}/location/${locationId}/floorMap`, {
        responseType: "blob",
        observe: "response",
      })

      .pipe(
        this.apiErrorHandler.handle("file"),
        catchError(() => of(null)),
        map((floormapSource) => {
          if (floormapSource) {
            const contentType = floormapSource.headers.get("content-type") ?? null;
            const type: "image" | "pdf" | null = this.parseFloormapType(contentType);
            return { type, source: floormapSource.body };
          } else {
            return null;
          }
        })
      );
  }

  private parseFloormapType(contentType: string | null) {
    let type: "image" | "pdf" | null = null;
    switch (contentType) {
      case "image/jpeg":
      case "image/png":
        type = "image";
        break;
      case "application/pdf":
        type = "pdf";
        break;
      default:
        break;
    }
    return type;
  }

  public getAuthenticatedImage(doorId: string, mediaId: string): Observable<Blob | null> {
    return this.http
      .get(`${environment.baseUrl}/gateSystem/${doorId}/images/${mediaId}`, {
        responseType: "blob",
      })
      .pipe(catchError(() => of(null)));
  }

  /**
   * Converts the server response body to a BotevoAsset object.
   * @param assetType The type of the asset to convert.
   * @param serverResBody The server response body containing the asset details.
   * @returns A BotevoAsset object representing the asset.
   * @throws An error if the asset type is not supported.
   */
  private convertToAsset(assetType: string, serverResBody: BotevoAssetDTO): BotevoAsset {
    switch (assetType) {
      case ASSET_TYPES.TENANT: {
        const tenantDTO = serverResBody as TenantDTO;
        return new Tenant(tenantDTO);
      }
      case ASSET_TYPES.CUSTOMER: {
        const customerDTO = serverResBody as CustomerDTO;
        return new Customer(customerDTO);
      }
      case ASSET_TYPES.LOCATION: {
        const locationDTO = serverResBody as LocationDTO;
        return new Location(locationDTO);
      }
      case ASSET_TYPES.BUILDING: {
        const buildingDTO = serverResBody as BuildingDTO;
        return new Building(buildingDTO);
      }
      case ASSET_TYPES.DOOR: {
        const doorDTO = serverResBody as DoorDTO;
        return new Door(doorDTO);
      }
      default:
        // Provide a default case to handle unexpected asset types
        throw new Error(`Unsupported asset type: ${assetType}`);
    }
  }
}
